import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import {
  FormValuesType,
  handleButtonRatingClickType,
  QuestionsRawType,
  SurveyType,
} from "types";

// Remove a small amount so that in the last page the progress bar is not full
const COMPLETION_OFFSET = 5; //The progress bar in the last page shouldn't be 100% full. This is the amount to deduct
const NORMALIZATION_VALUE = 100;
const THANK_YOU_PAGE = "thankyou";
const MIN_RANGE_VALUE = 1;

export const useSurveyState = (surveys: SurveyType[]) => {
  const navigate = useNavigate();

  const { surveyCode, uuid, answer } = useParams();

  const [formValues, setFormValues] = useState<FormValuesType[]>(() => {
    const localStorageSurveyData = JSON.parse(
      localStorage.getItem(`DilsSurvey-${uuid}`) ?? "{}"
    );

    if (!localStorageSurveyData.formValues) {
      return [];
    }
    return localStorageSurveyData.formValues;
  });

  const SURVEY_LOCAL_KEY = `DilsSurvey-${uuid}`;

  const questionsRaw: QuestionsRawType[] = useMemo(
    () =>
      surveys.find((survey) => survey.surveyCode === surveyCode)?.survey ?? [],
    [surveyCode, surveys]
  );

  const questions = useMemo(
    () => questionsRaw.flatMap((qR) => qR.questions),
    [questionsRaw]
  );

  const [currentSection, setCurrentSection] = useState<number>(() => {
    const localStorageSurveyData = JSON.parse(
      localStorage.getItem(SURVEY_LOCAL_KEY) ?? "{}"
    );

    if (
      localStorageSurveyData.uuid &&
      localStorageSurveyData.uuid === uuid &&
      localStorageSurveyData.currentSection
    ) {
      return localStorageSurveyData.currentSection;
    } else {
      return 1;
    }
  });
  const [isSendingSurvey, setIsSendingSurvey] = useState(false);

  const sectionsNumber = useMemo(() => {
    return questionsRaw[questionsRaw.length - 1].sectionNumber;
  }, [questionsRaw]);

  const [currentPercentage, setCurrentPercentage] = useState<number>(0);

  const nextPercentage = useMemo<number>(
    () => (currentSection / sectionsNumber) * 100 - COMPLETION_OFFSET,
    [currentSection, sectionsNumber]
  );

  const handleSubmit = async () => {
    try {
      setIsSendingSurvey(true);
      const normalizedValues = formValues.map((value) => {
        return {
          ...value,
          answer: value.measurable
            ? ((value.answer! - MIN_RANGE_VALUE) * NORMALIZATION_VALUE) /
              (value.range - MIN_RANGE_VALUE)
            : value.answer,

          openAnswer: value.measurable
            ? value.openAnswer
            : `${value?.answersMeaning?.[value.answer!]} ${
                value.openAnswer ?? ""
              }`,
        };
      });

      if (!process.env.REACT_APP_DATAHUB_URL) {
        throw new Error("URL not found");
      }

      const request = await fetch(process.env.REACT_APP_DATAHUB_URL, {
        method: "POST",
        body: JSON.stringify({
          type: "send-customer-survey",
          payload: {
            uuid,
            answers: normalizedValues,
            questions: questions,
          },
        }),
        headers: {
          "Content-Type": "application/json",
          "X-Dils-Api-Key": process.env.REACT_APP_DILS_API_KEY ?? "",
        },
      });

      if (!request.ok) {
        throw new Error(`HTTP error! Status: ${request.status}`);
      }

      setIsSendingSurvey(false);

      if (currentSection === sectionsNumber) {
        navigate(`/${THANK_YOU_PAGE}`);
      } else {
        setCurrentSection((prevSection) => prevSection + 1);
        setCurrentPercentage((currentSection / sectionsNumber) * 100);
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        confirmButtonText: "Chiudi",
        html: '<span style="font-size:16px">Impossibile inviare il sondaggio.<br />Si prega di riprovare più avanti</span>',
        confirmButtonColor: "#404254",
      });
      setIsSendingSurvey(false);

      console.error("Request failed", error);
    }
  };

  const handleButtonRatingClick: handleButtonRatingClickType = useCallback(
    (sectionCode, questionId, answer, range, measurable = true) => {
      const questionToUpdate = (questions ?? []).find(
        (q) => q.id === questionId
      );

      setFormValues((prevFormValues) => {
        const existingAnswerIndex = prevFormValues.findIndex(
          (item) => item.questionId === questionId
        );

        if (existingAnswerIndex >= 0) {
          const updatedFormValues = [...prevFormValues];

          updatedFormValues[existingAnswerIndex] = {
            sectionCode,
            questionId,
            question: questionToUpdate?.text!,
            answer,
            range,
            measurable,
            answersMeaning: questionToUpdate?.answersMeaning ?? {},
            ...(questionToUpdate?.condition
              ? { condition: questionToUpdate.condition }
              : {}),
          };
          return updatedFormValues;
        } else {
          return [
            ...prevFormValues,
            {
              sectionCode,
              questionId,
              question: questionToUpdate?.text!,
              answer,
              range,
              measurable,
              answersMeaning: questionToUpdate?.answersMeaning,
              ...(questionToUpdate?.condition
                ? { condition: questionToUpdate.condition }
                : {}),
            },
          ];
        }
      });
    },
    [questions]
  );

  const handleBackButton = () => {
    setCurrentSection((prev) => prev - 1);
  };

  // Populate the first answer coming from the email
  useEffect(() => {
    if (!answer) {
      return;
    }
    const answerNumber = parseInt(answer);
    if (answerNumber > 0) {
      handleButtonRatingClick(
        questionsRaw[0].sectionCode,
        questions[0].id,
        answerNumber,
        5
      );
    }
  }, [answer, handleButtonRatingClick, questions, questionsRaw]);

  // Scroll to the top at any new section
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentSection]);

  // Save current section in local storage
  useEffect(() => {
    localStorage.setItem(
      SURVEY_LOCAL_KEY,
      JSON.stringify({ currentSection, uuid, formValues })
    );
  }, [SURVEY_LOCAL_KEY, currentSection, formValues, uuid]);

  return {
    handleButtonRatingClick,
    handleSubmit,
    formValues,
    setFormValues,
    currentSection,
    setCurrentSection,
    sectionsNumber,
    currentPercentage,
    setCurrentPercentage,
    nextPercentage,
    isSendingSurvey,
    handleBackButton,
    questionsRaw,
  };
};
